import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CompassRose, Sparkle, Brain } from "@phosphor-icons/react";
import JourneyAvatar from "../components/JourneyAvatar";
import ChatMessage, { TypingIndicator } from "../components/ChatMessage";
import ChatInput from "../components/ChatInput";
import SaveMemoryDialog from "../components/SaveMemoryDialog";
import SuggestionsSheet from "../components/SuggestionsSheet";
import * as API from "../lib/api";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";

const SUGGESTIONS = [
  "Summarize what's on my plate this week",
  "Help me draft a message to a colleague",
  "Brainstorm ideas for a weekend project",
  "Explain a complex topic simply",
];

export default function Chat() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeConvId, setActiveConvId, refreshConversations, settings } = useApp();
  const tz = settings?.tz || "America/Los_Angeles";

  const convFromUrl = searchParams.get("c");
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  // assistant-message-id -> [reminder, ...] created during that turn
  const [remindersByMessage, setRemindersByMessage] = useState({});
  // assistant-message-id -> [task, ...] delegated during that turn (V9.27)
  const [tasksByMessage, setTasksByMessage] = useState({});
  // assistant-message-id -> [visualSentResult, ...] (V9.35)
  const [visualsSentByMessage, setVisualsSentByMessage] = useState({});
  // assistant-message-id -> step_guide payload (with base64 images)
  const [stepGuidesByMessage, setStepGuidesByMessage] = useState({});
  // assistant-message-id -> latest first-person status line + counters
  const [stepStatusByMessage, setStepStatusByMessage] = useState({});
  // assistant-message-id -> true when the LLM called show_my_face
  const [faceShownByMessage, setFaceShownByMessage] = useState({});
  // assistant-message-id -> [{tool_name, fetched_at, summary}, ...]
  // V9.26 — track every realtime tool result emitted via SSE so the
  // assistant bubble can render a "Live · 2:31 PM" badge below the
  // text, making it crystal-clear which info is live vs remembered.
  const [realtimeByMessage, setRealtimeByMessage] = useState({});
  // V9.44 — assistant-message-id -> latest reminder_lookup payload
  // so the bubble can render a ReminderLookupChip below the text.
  const [reminderLookupByMessage, setReminderLookupByMessage] = useState({});
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [saveMemoryOpen, setSaveMemoryOpen] = useState(false);
  const [saveMemoryContent, setSaveMemoryContent] = useState("");

  const [pendingSuggestions, setPendingSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  // Map of user-message-id -> inline suggestion object
  const [inlineByMessage, setInlineByMessage] = useState({});

  const scrollRef = useRef(null);
  const streamCtlRef = useRef(null);
  // When a new conversation is created mid-stream, the URL → activeConvId
  // → history-reload chain would otherwise wipe the in-flight assistant
  // message. We mark the next reload to be skipped just for that convoId.
  const skipNextHistoryLoadRef = useRef(null);
  // V9.26.5 — Stale-closure backstop. The SSE handlers below reference
  // these refs instead of plain `let` locals so that, no matter how many
  // re-renders happen mid-stream in long-running chats, every handler
  // (even ones queued via React 18 batching) reads the SAME, currently-
  // active assistant/user message ids. Without this, in long sessions
  // with heavy history we observed the reminder chip silently failing
  // to attach because a deferred render captured the tmp id while
  // `setRemindersByMessage` had already written the real id.
  const assistantIdRef = useRef(null);
  const savedUserMsgIdRef = useRef(null);
  const receivedConvIdRef = useRef(null);

  const openSaveMemory = useCallback((content) => {
    setSaveMemoryContent(content);
    setSaveMemoryOpen(true);
  }, []);

  const resolveInline = useCallback((suggestionId) => {
    setInlineByMessage((prev) => {
      const next = { ...prev };
      for (const k of Object.keys(next)) {
        if (next[k]?.id === suggestionId) delete next[k];
      }
      return next;
    });
  }, []);

  const refreshSuggestions = useCallback(async () => {
    try {
      const list = await API.listSuggestions("pending");
      setPendingSuggestions(list);
    } catch {
      // never crash chat on suggestion failure
    }
  }, []);

  // Load suggestions on mount
  useEffect(() => {
    refreshSuggestions();
  }, [refreshSuggestions]);

  useEffect(() => {
    if (convFromUrl && convFromUrl !== activeConvId) {
      setActiveConvId(convFromUrl);
    } else if (!convFromUrl && activeConvId) {
      setActiveConvId(null);
    }
  }, [convFromUrl]); // eslint-disable-line

  useEffect(() => {
    if (!activeConvId) {
      setMessages([]);
      return;
    }
    // If we just created this convo as part of an active stream, skip the
    // reload so the streaming assistant message isn't wiped.
    if (skipNextHistoryLoadRef.current === activeConvId) {
      skipNextHistoryLoadRef.current = null;
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingHistory(true);
      try {
        const hist = await API.getHistory(activeConvId);
        if (cancelled) return;
        setMessages(hist);
        // Restore step_guides attached to historical messages so cards
        // re-render on reload (covers mid-stream client-disconnect cases
        // where the SSE event was never received). On reload, any null
        // image is treated as "failed to generate" (we're done streaming),
        // so the card renders the graceful "No visual" fallback instead
        // of a perpetual spinner.
        const guides = {};
        const facesShown = {};
        const visualsSent = {};
        const tasksDelegated = {};
        const reminderLookups = {};
        for (const m of hist) {
          const sg = m?.metadata?.step_guide;
          if (sg && Array.isArray(sg.steps)) {
            const normalized = {
              ...sg,
              steps: sg.steps.map((s) => ({ ...s, image_failed: !s.image_b64 })),
            };
            guides[m.id] = normalized;
          }
          if (m?.metadata?.show_face) facesShown[m.id] = true;
          // V9.36 — Restore visual-sent-to-contact chips so the user
          // sees the persisted dispatch confirmation after a reload
          // (and the LLM no longer regenerates the guide when the
          // owner asks "did you send it to Lynn?").
          const vs = m?.metadata?.visuals_sent;
          if (Array.isArray(vs) && vs.length > 0) {
            visualsSent[m.id] = vs;
          }
          // V9.38 — Same for delegated tasks so the `TaskDelegatedChip`s
          // re-render after a page reload. Without this, refreshing
          // the page hid the chip and the user thought the task hadn't
          // been created — even though the task row was alive in the
          // Tasks UI.
          const td = m?.metadata?.tasks_delegated;
          if (Array.isArray(td) && td.length > 0) {
            tasksDelegated[m.id] = td;
          }
          // V9.44 — Restore reminder_lookup card on reload so the
          // verification answer ("did Lynn's reminder fire?") still
          // renders the chip with verdict colors after refresh.
          const rl = m?.metadata?.reminder_lookup;
          if (rl && Array.isArray(rl.reminders)) {
            reminderLookups[m.id] = rl;
          }
        }
        setStepGuidesByMessage((prev) => ({ ...prev, ...guides }));
        setFaceShownByMessage((prev) => ({ ...prev, ...facesShown }));
        setVisualsSentByMessage((prev) => ({ ...prev, ...visualsSent }));
        setTasksByMessage((prev) => ({ ...prev, ...tasksDelegated }));
        setReminderLookupByMessage((prev) => ({ ...prev, ...reminderLookups }));
      } catch {
        if (!cancelled) toast.error("Failed to load conversation");
      } finally {
        if (!cancelled) setLoadingHistory(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeConvId]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  // Cancel any active stream on unmount
  useEffect(() => {
    return () => streamCtlRef.current?.abort();
  }, []);

  // Per-card regenerate handler. Marks the card as drawing, calls the
  // backend to regenerate that single step's icon, then writes the new
  // (or null, on failure) base64 back into stepGuidesByMessage.
  const handleRegenerateStep = useCallback(async (messageId, stepIndex) => {
    setStepGuidesByMessage((prev) => {
      const cur = prev[messageId];
      if (!cur || !Array.isArray(cur.steps)) return prev;
      if (stepIndex < 0 || stepIndex >= cur.steps.length) return prev;
      const nextSteps = cur.steps.map((s, i) =>
        i === stepIndex ? { ...s, image_b64: null, image_failed: false, regenerating: true } : s
      );
      return { ...prev, [messageId]: { ...cur, steps: nextSteps } };
    });
    try {
      const res = await API.regenerateStepImage(messageId, stepIndex);
      setStepGuidesByMessage((prev) => {
        const cur = prev[messageId];
        if (!cur || !Array.isArray(cur.steps)) return prev;
        const nextSteps = cur.steps.map((s, i) =>
          i === stepIndex
            ? {
                ...s,
                image_b64: res?.image_b64 || null,
                image_failed: !res?.image_b64,
                regenerating: false,
              }
            : s
        );
        return { ...prev, [messageId]: { ...cur, steps: nextSteps } };
      });
      if (!res?.ok) {
        toast.error("Couldn't redraw that step. Try again?");
      }
    } catch (e) {
      setStepGuidesByMessage((prev) => {
        const cur = prev[messageId];
        if (!cur || !Array.isArray(cur.steps)) return prev;
        const nextSteps = cur.steps.map((s, i) =>
          i === stepIndex ? { ...s, image_failed: true, regenerating: false } : s
        );
        return { ...prev, [messageId]: { ...cur, steps: nextSteps } };
      });
      toast.error("Network error while redrawing.");
    }
  }, []);

  const handleSend = useCallback(
    (text, fileIds) => {
      if (streaming) return;
      const tmpUserId = `tmp-user-${Date.now()}`;
      const tmpAssistantId = `tmp-asst-${Date.now()}`;

      setMessages((m) => [
        ...m,
        {
          id: tmpUserId,
          conversation_id: activeConvId || "",
          role: "user",
          content: text,
          file_ids: fileIds,
          created_at: new Date().toISOString(),
        },
        {
          id: tmpAssistantId,
          conversation_id: activeConvId || "",
          role: "assistant",
          content: "",
          file_ids: [],
          created_at: new Date().toISOString(),
        },
      ]);
      setStreaming(true);

      // V9.26.5 — Use refs (not closure-captured `let` vars) for the live
      // conversation/message ids. SSE handlers below all read these via
      // `*Ref.current`, eliminating the stale-closure window that caused
      // reminder chips to silently miss in long-running chats.
      assistantIdRef.current = tmpAssistantId;
      receivedConvIdRef.current = activeConvId;
      savedUserMsgIdRef.current = null;

      streamCtlRef.current = API.streamChat(
        {
          conversation_id: activeConvId || null,
          content: text,
          file_ids: fileIds,
        },
        {
          onMeta: (meta) => {
            // Update refs so every other handler closure (including any
            // already-queued ones) sees the canonical ids immediately.
            receivedConvIdRef.current = meta.conversation_id;
            assistantIdRef.current = meta.assistant_message_id;
            savedUserMsgIdRef.current = meta.user_message?.id || null;
            setMessages((m) =>
              m.map((x) =>
                x.id === tmpUserId
                  ? { ...meta.user_message }
                  : x.id === tmpAssistantId
                  ? {
                      ...x,
                      id: meta.assistant_message_id,
                      conversation_id: meta.conversation_id,
                    }
                  : x
              )
            );
            if (!activeConvId) {
              skipNextHistoryLoadRef.current = meta.conversation_id;
              setActiveConvId(meta.conversation_id);
              navigate(`/app?c=${meta.conversation_id}`, { replace: true });
            }
          },
          onToken: ({ text: t }) => {
            const id = assistantIdRef.current;
            setMessages((m) =>
              m.map((x) =>
                x.id === id ? { ...x, content: x.content + t } : x
              )
            );
          },
          onReminderCreated: (rem) => {
            // Always pull the LIVE id from the ref so a deferred handler
            // can never write into a stale tmp-id bucket. Without this,
            // in long-running chats we'd occasionally see `reminder_created`
            // land before React flushed the message-id swap from onMeta,
            // and the chip would attach to a key that no message ever
            // matched in render.
            const id = assistantIdRef.current;
            setRemindersByMessage((prev) => ({
              ...prev,
              [id]: [...(prev[id] || []), rem],
            }));
            // Notify other listeners (e.g. /reminders page) to refresh.
            try {
              window.dispatchEvent(
                new CustomEvent("journey:reminder-created", { detail: rem })
              );
            } catch {}
          },
          onTaskDelegated: (task) => {
            // V9.27 — Mirror the reminder-chip pattern. Read the live id
            // from the ref so the chip never misses in long chats.
            const id = assistantIdRef.current;
            setTasksByMessage((prev) => ({
              ...prev,
              [id]: [...(prev[id] || []), task],
            }));
            // Notify /tasks listeners so they re-fetch and surface the
            // new delegated task immediately.
            try {
              window.dispatchEvent(
                new CustomEvent("journey:task-delegated", { detail: task })
              );
            } catch {}
          },
          onVisualSentToContact: (res) => {
            // V9.35 — Owner-side visual delivery confirmation. The
            // backend has already spawned the MMS dispatch when this
            // event fires; the chip just confirms the LLM called the
            // right tool with the right contact.
            const id = assistantIdRef.current;
            setVisualsSentByMessage((prev) => ({
              ...prev,
              [id]: [...(prev[id] || []), res],
            }));
          },
          onStepGuideStart: ({ guide }) => {
            const id = assistantIdRef.current;
            // Backend sent the skeleton — render numbered cards immediately
            // with placeholders. Each card's image fills in via onStepImage.
            setStepGuidesByMessage((prev) => ({
              ...prev,
              [id]: guide,
            }));
            setStepStatusByMessage((prev) => ({
              ...prev,
              [id]: {
                completed: 0,
                total: (guide?.steps || []).length,
                message: "I'm putting together your visual guide…",
                kind: "working",
              },
            }));
          },
          onStepGuideStatus: ({ completed, total, message, kind }) => {
            const id = assistantIdRef.current;
            setStepStatusByMessage((prev) => ({
              ...prev,
              [id]: { completed, total, message, kind },
            }));
          },
          onShowFace: () => {
            const id = assistantIdRef.current;
            setFaceShownByMessage((prev) => ({ ...prev, [id]: true }));
          },
          onRealtimeResult: ({ tool_name, result }) => {
            const id = assistantIdRef.current;
            // V9.26 — Record the tool result alongside the assistant
            // turn so the chat bubble can render a live-data chip.
            setRealtimeByMessage((prev) => {
              const cur = prev[id] || [];
              return {
                ...prev,
                [id]: [...cur, { tool_name, result }],
              };
            });
          },
          onReminderLookup: (payload) => {
            // V9.44 — attach the lookup payload to the assistant
            // message so the bubble renders a ReminderLookupChip card
            // (collapsed one-liner per match → expandable details).
            const id = assistantIdRef.current;
            setReminderLookupByMessage((prev) => ({ ...prev, [id]: payload }));
          },
          onStepImage: ({ step_index, image_b64 }) => {
            const id = assistantIdRef.current;
            setStepGuidesByMessage((prev) => {
              const cur = prev[id];
              if (!cur || !Array.isArray(cur.steps)) return prev;
              if (step_index < 0 || step_index >= cur.steps.length) return prev;
              // Distinguish "still drawing" (image_b64 stays null until an
              // event arrives) from "tried, failed" (event arrived with
              // image_b64=null) so the card can swap the spinner for a
              // graceful no-visual fallback.
              const nextSteps = cur.steps.map((s, i) =>
                i === step_index
                  ? { ...s, image_b64, image_failed: !image_b64 }
                  : s
              );
              return { ...prev, [id]: { ...cur, steps: nextSteps } };
            });
          },
          onStepGuide: ({ guide }) => {
            const id = assistantIdRef.current;
            // Legacy single-shot event (kept for backwards compat).
            setStepGuidesByMessage((prev) => ({
              ...prev,
              [id]: guide,
            }));
          },
          onDone: async (payload) => {
            const { assistant_message, reminders_created } = payload || {};
            const id = assistantIdRef.current;
            const finalConvId = receivedConvIdRef.current;
            const finalUserMsgId = savedUserMsgIdRef.current;
            setMessages((m) =>
              m.map((x) =>
                x.id === id ? { ...assistant_message } : x
              )
            );
            // V9.26.5 — Reminder-chip backstop. The `done` payload carries
            // the canonical list of reminders created on this turn. We
            // reconcile (merge by id) into remindersByMessage so the blue
            // chip always renders even if an individual `reminder_created`
            // SSE event was dropped, batched into a stale render, or
            // arrived before onMeta could update the id ref. Fixes the
            // long-running-chat regression where the chip silently went
            // missing after many turns.
            if (
              id &&
              Array.isArray(reminders_created) &&
              reminders_created.length > 0
            ) {
              setRemindersByMessage((prev) => {
                const existing = prev[id] || [];
                const byId = new Map(existing.map((r) => [r.id, r]));
                for (const r of reminders_created) {
                  if (r && r.id) byId.set(r.id, r);
                }
                return { ...prev, [id]: Array.from(byId.values()) };
              });
              // Mirror the side-effect of `onReminderCreated` so the
              // /reminders page also refreshes when chips were only
              // backstopped here (and not via the per-event handler).
              try {
                for (const r of reminders_created) {
                  window.dispatchEvent(
                    new CustomEvent("journey:reminder-created", { detail: r })
                  );
                }
              } catch {}
            }
            setStreaming(false);
            // Stream is done — clear any in-progress step-guide status
            // badge so users see the final assistant text + cards cleanly.
            setStepStatusByMessage((prev) => {
              const next = { ...prev };
              delete next[id];
              return next;
            });
            // Refresh sidebar list
            refreshConversations();

            // Inline high-confidence suggestion (anchored to user message) — fire and forget
            if (finalUserMsgId && finalConvId) {
              API.inlineSuggestion(finalConvId, finalUserMsgId)
                .then((s) => {
                  if (s && s.id) {
                    setInlineByMessage((prev) => ({
                      ...prev,
                      [finalUserMsgId]: s,
                    }));
                  }
                })
                .catch(() => {});
            }

            // Bulk suggestions for longer conversations (kept; cooldown handles dupes)
            if (finalConvId) {
              API.generateSuggestions(finalConvId)
                .then((newOnes) => {
                  if (newOnes && newOnes.length > 0) {
                    setPendingSuggestions((prev) => [...newOnes, ...prev]);
                    toast(`${newOnes.length} memory ${newOnes.length === 1 ? "suggestion" : "suggestions"}`, {
                      description: "Tap the brain icon to review",
                      duration: 5000,
                    });
                  }
                })
                .catch(() => {});
            }
          },
          onError: (e) => {
            const id = assistantIdRef.current;
            setStreaming(false);
            // If we got no tokens, show the error inline; if we got partial text, keep it
            setMessages((m) =>
              m.map((x) =>
                x.id === id && !x.content
                  ? {
                      ...x,
                      content:
                        "Sorry, I had trouble reaching the AI service. Please try again in a moment.",
                    }
                  : x
              )
            );
            console.error("stream error", e);
          },
        }
      );
    },
    [activeConvId, streaming, navigate, setActiveConvId, refreshConversations]
  );

  const empty = !activeConvId && messages.length === 0;
  const pendingCount = pendingSuggestions.length;

  return (
    <div className="flex flex-col h-full" data-testid="chat-page">
      {pendingCount > 0 && (
        <SuggestionsBanner
          count={pendingCount}
          onOpen={() => setSuggestionsOpen(true)}
        />
      )}
      <div ref={scrollRef} className="flex-1 overflow-y-auto scroll-contain">
        {empty ? (
          <div className="min-h-full flex items-center justify-center px-6 py-16">
            <div className="max-w-xl w-full text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="inline-flex mb-6"
              >
                <JourneyAvatar size={88} testId="empty-state-avatar" />
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05 }}
                className="font-display text-3xl sm:text-4xl font-semibold tracking-tight"
              >
                Where shall we go today?
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="text-white/60 mt-3 text-[15px]"
              >
                Your personal AI companion. Ask, plan, remember.
              </motion.p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-8">
                {SUGGESTIONS.map((s, i) => (
                  <motion.button
                    key={s}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: 0.15 + i * 0.04 }}
                    onClick={() => handleSend(s, [])}
                    className="text-left px-4 py-3.5 rounded-2xl bg-[#0f0f0f] hover:bg-[#161616] border border-white/5 hover:border-cyan-500/30 transition-all text-sm text-white/80 group"
                    data-testid={`suggestion-${i}`}
                  >
                    <div className="flex items-start gap-2">
                      <Sparkle
                        size={14}
                        weight="fill"
                        className="mt-0.5 text-cyan-400/60 group-hover:text-cyan-400 transition-colors shrink-0"
                      />
                      <span>{s}</span>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="px-3 sm:px-6 py-6 max-w-3xl mx-auto space-y-5">
            {loadingHistory && (
              <div className="text-center text-white/40 text-sm">Loading…</div>
            )}
            {messages.map((m, i) => {
              const isLastAssistantStreaming =
                streaming &&
                m.role === "assistant" &&
                i === messages.length - 1;
              return (
                <ChatMessage
                  key={m.id}
                  message={m}
                  index={i}
                  onSaveAsMemory={openSaveMemory}
                  inlineSuggestion={inlineByMessage[m.id]}
                  onInlineResolved={resolveInline}
                  remindersCreated={remindersByMessage[m.id]}
                  tasksDelegated={tasksByMessage[m.id]}
                  visualsSentToContact={visualsSentByMessage[m.id]}
                  stepGuide={stepGuidesByMessage[m.id]}
                  stepStatus={stepStatusByMessage[m.id]}
                  showFace={faceShownByMessage[m.id]}
                  realtimeResults={realtimeByMessage[m.id]}
                  reminderLookup={reminderLookupByMessage[m.id]}
                  onRegenerateStep={handleRegenerateStep}
                  tz={tz}
                  isStreaming={isLastAssistantStreaming}
                />
              );
            })}
            {streaming && messages.length === 0 && <TypingIndicator />}
          </div>
        )}
      </div>
      <ChatInput
        onSend={handleSend}
        disabled={streaming}
        draftKey={activeConvId || "new"}
      />
      <SaveMemoryDialog
        open={saveMemoryOpen}
        onOpenChange={setSaveMemoryOpen}
        sourceContent={saveMemoryContent}
      />
      <SuggestionsSheet
        open={suggestionsOpen}
        onOpenChange={setSuggestionsOpen}
        suggestions={pendingSuggestions}
        onChange={setPendingSuggestions}
      />
    </div>
  );
}

function SuggestionsBanner({ count, onOpen }) {
  return (
    <AnimatePresence>
      <motion.button
        layout
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        onClick={onOpen}
        data-testid="suggestions-banner"
        className="mx-3 sm:mx-6 mt-3 flex items-center gap-3 px-4 py-2.5 rounded-2xl bg-gradient-to-r from-cyan-500/10 via-cyan-500/5 to-transparent border border-cyan-500/25 hover:border-cyan-500/40 transition-colors group"
      >
        <div className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center shrink-0">
          <Brain size={16} weight="duotone" className="text-cyan-400" />
        </div>
        <div className="flex-1 text-left">
          <div className="text-[13px] text-white/90 font-medium">
            {count} memory {count === 1 ? "suggestion" : "suggestions"} for review
          </div>
          <div className="text-[11px] text-white/50">
            Tap to accept, edit, or ignore
          </div>
        </div>
        <span className="text-cyan-400 text-xs group-hover:translate-x-0.5 transition-transform">
          Review →
        </span>
      </motion.button>
    </AnimatePresence>
  );
}
